import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FormDatePicker } from '@/components/form/FormDatePicker';
import { FormSection } from '@/components/form/FormSection';
import FormSwitch from '@/components/form/FormSwitch';
import { Domain } from '@/features/domains';

import { ReportFormProps } from '../../types';
import { downloadReport } from '../../utils/downloadReport';
import { handleDownloadReportError } from '../../utils/handleDownloadReportError';
import { ReportModalForm } from '../ReportModalForm';
import { SchemasPicker } from '../SchemasPicker';

type Form = {
  month: Date | null;
  specificDate: Date | null;
  useSpecificDate: boolean;
  schemas: Array<Domain>;
  splitByType: boolean;
  splitByFacility: boolean;
  containsArchived: boolean;
  hideSuspendedAndCollection: boolean;
  useInterestSnapshots: boolean;
};

const INITIAL_VALUES = {
  month: new Date(),
  specificDate: null,
  useSpecificDate: false,
  schemas: [],
  splitByType: false,
  splitByFacility: false,
  containsArchived: false,
  hideSuspendedAndCollection: false,
  useInterestSnapshots: true,
};

const schema = yup.object({
  month: yup.date().required('Pole wymagane'),
  specificDate: yup
    .date()
    .nullable()
    .when('useSpecificDate', {
      is: true,
      then: (value) => value.required('Pole wymagane'),
      otherwise: (value) => value.nullable(),
    }),
  useSpecificDate: yup.boolean().required(),
  schemas: yup.array().min(1, 'Pole wymagane'),
  splitByType: yup.boolean().required(),
  splitByFacility: yup.boolean().required(),
  containsArchived: yup.boolean().required(),
  hideSuspendedAndCollection: yup.boolean().required(),
  useInterestSnapshots: yup.boolean().required(),
});

export const StatementOfInterestForm = (props: ReportFormProps) => {
  const { report } = props;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = useForm<Form>({
    defaultValues: INITIAL_VALUES,
    resolver: yupResolver(schema),
  });

  const useSpecificDate = watch('useSpecificDate');

  useEffect(() => {
    if (!useSpecificDate) {
      setValue('specificDate', null);
    }
  }, [setValue, useSpecificDate]);

  const handleFormSubmit = async (values: Form) => {
    try {
      const params: Record<string, any> = {
        month: dayjs(values.month).format('YYYY-MM'),
        schemas: values.schemas.map((domain) => domain.domain),
        splitByType: values.splitByType,
        splitByFacility: values.splitByFacility,
        containsArchived: values.containsArchived,
        hideSuspendedAndCollection: values.hideSuspendedAndCollection,
        useInterestSnapshots: values.useInterestSnapshots,
      };

      if (values.useSpecificDate && values.specificDate) {
        params.specificDate = dayjs(values.specificDate).format('YYYY-MM-DD');
      }

      await downloadReport(report, params);
      props.onClose();
    } catch (err: any) {
      handleDownloadReportError(err);
    }
  };

  return (
    <ReportModalForm
      {...props}
      onSubmit={handleSubmit(handleFormSubmit)}
      isSubmitting={isSubmitting}
    >
      <FormSection title={'Placówki'}>
        <SchemasPicker control={control} />
      </FormSection>
      <FormSection title={'Zakres'}>
        <FormSwitch
          control={control}
          name="useSpecificDate"
          label="Generuj raport na konkretny dzień"
        />
        {useSpecificDate ? (
          <FormDatePicker
            control={control}
            name={'specificDate'}
            placeholderText={'Wybierz dzień'}
          />
        ) : (
          <FormDatePicker
            control={control}
            name={'month'}
            placeholderText={'Wybierz miesiąc'}
            showMonthYearPicker
            dateFormat={'MM-yyyy'}
          />
        )}
      </FormSection>
      <FormSection title={'Opcje raportu'}>
        <FormSwitch control={control} name="useInterestSnapshots" label="Użyj snapshotów odsetek" />
        <FormSwitch
          control={control}
          name="splitByType"
          label="Pokaż kwoty poszczególnych typów odsetek"
        />
        <FormSwitch
          control={control}
          name="splitByFacility"
          label="Podziel raport na osobne tabele dla każdej placówki"
        />
        <FormSwitch
          control={control}
          name="containsArchived"
          label="Uwzględnij dzieci zarchiwizowane, jeżeli posiadają odsetki w wybranym miesiącu"
        />
        <FormSwitch
          control={control}
          name="hideSuspendedAndCollection"
          label="Ukryj dzieci zawieszone i objęte windykacją"
        />
      </FormSection>
    </ReportModalForm>
  );
};
